// =====================================================
// BED — Zone administrative (migration 00178)
// =====================================================
// Accès staff aux données du bulletin d'engagement d'un équipage : état,
// éléments manquants, levées manuelles, override de statut, émission du lien
// du FORMULAIRE.
//
// Toutes les RPC portent leur propre garde de rôle côté serveur
// (`superadmin`/`admin`, 42501 sinon) : ce module ne rejoue aucun contrôle
// d'accès, il traduit les erreurs nommées en statut discriminé — comme le reste
// du module BED (cf. `src/lib/bed/draft.ts`).

import { supabase } from '../supabase';
import { ROUTES } from '../routes/constants';

// -----------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------

/** Élément manquant tel que la zone admin le voit : celui de 00177 + la levée. */
export interface BedAdminMissingItem {
  code: string;
  kind: 'deferred' | 'empty';
  label: string;
  role: string;
  role_label: string;
  /** Date d'engagement « je communique avant le … » (kind === 'deferred'). */
  due_date?: string | null;
  /** Levé à la main par l'administration. */
  resolved: boolean;
  resolved_at?: string | null;
  resolved_by?: string | null;
  note?: string | null;
}

export interface BedAdminSignature {
  data_url: string;
  signed_at: string;
}

export interface BedAdminCertificate {
  path: string;
  name: string;
  size: number;
  uploaded_at?: string;
}

export interface BedAdminMissingSnapshot {
  /** D'où vient la liste : brouillon du lien, dernière soumission, ou rien. */
  source: 'draft' | 'submission' | 'none';
  items: BedAdminMissingItem[];
  /** Ce qui manque ENCORE (levées manuelles déduites). */
  outstanding_count: number;
  resolved_count: number;
  submitted_at: string | null;
  /** `{ "<groupe>:<rôle>": { due_date } }` — engagements « avant le … ». */
  deferred: Record<string, { due_date?: string }>;
  signatures: Partial<Record<'pilot' | 'copilot', BedAdminSignature>>;
  certificates: Partial<Record<'pilot' | 'copilot', BedAdminCertificate>>;
  draft_id: string | null;
  bed_access_token_id: string | null;
}

export interface BedAdminPaymentState {
  exists: boolean;
  settled: boolean;
  payment_case_id?: string;
  status?: string;
  amount_cents?: number;
  paid_cents?: number;
  remaining_cents?: number;
  order_id?: string;
  method?: string | null;
}

export interface BedAdminLink {
  bed_access_token_id: string;
  token: string;
  created_at: string;
  expires_at: string;
  expired: boolean;
}

export interface BedCrewOverview {
  crew_id: string;
  bed_status: string;
  bed_status_label: string;
  /** Statut que le dossier DEVRAIT porter (null si le BED n'est pas engagé). */
  target_status: string | null;
  /** Tout est réglé et complet, mais le passage en Inscrit déf. reste manuel. */
  ready_for_final_registration: boolean;
  missing: BedAdminMissingSnapshot;
  payment: BedAdminPaymentState;
  info_deadline: string | null;
  link: BedAdminLink | null;
}

/** Synthèse BED d'un équipage, pour les listes et l'export CSV. */
export interface BedCrewSummary {
  crew_id: string;
  bed_status: string;
  bed_status_label: string;
  bed_missing_count: number;
  bed_resolved_count: number;
  bed_missing_labels: string;
  bed_submitted_at: string | null;
  bed_payment_status: string | null;
  bed_amount_cents: number | null;
  bed_paid_cents: number | null;
  bed_remaining_cents: number | null;
  bed_payment_settled: boolean;
}

export type BedAdminError = 'forbidden' | 'not_found' | 'no_paid_bpi_parent' | 'error';

export type BedOverviewResult =
  | { status: 'ok'; data: BedCrewOverview }
  | { status: BedAdminError; message?: string };

export type BedMintResult =
  | { status: 'ok'; token: string; url: string; expiresInDays: number }
  | { status: BedAdminError; message?: string };

// -----------------------------------------------------------------------
// MAPPING D'ERREURS
// -----------------------------------------------------------------------

function classify(message: string | undefined): BedAdminError {
  if (!message) return 'error';
  if (message.includes('insufficient_privilege')) return 'forbidden';
  if (message.includes('no_paid_bpi_parent')) return 'no_paid_bpi_parent';
  if (message.includes('not_found')) return 'not_found';
  return 'error';
}

/** Message actionnable pour l'admin, jamais le message brut de PostgreSQL. */
export function bedAdminErrorMessage(error: BedAdminError): string {
  switch (error) {
    case 'forbidden':
      return "Vous n'avez pas les droits sur la zone BED.";
    case 'no_paid_bpi_parent':
      return 'Aucun BPI payé pour cet équipage : le lien BED ne peut pas être émis.';
    case 'not_found':
      return 'Équipage introuvable.';
    default:
      return 'Une erreur est survenue.';
  }
}

// -----------------------------------------------------------------------
// LIEN PUBLIC DU FORMULAIRE
// -----------------------------------------------------------------------

/**
 * URL complète du FORMULAIRE BED pour un token donné.
 *
 * À ne pas confondre avec `buildBedPaymentLink` (`src/hooks/useBedActions.ts`),
 * qui pointe vers le tunnel de PAIEMENT direct côté 4LT. Ici c'est le bulletin
 * lui-même, servi par le CRM (`PublicFormPage` → `BedWizard`).
 */
export function buildBedFormLink(slug: string, token: string): string {
  return `${window.location.origin}${ROUTES.PUBLIC.FORM(slug)}?token=${token}`;
}

// -----------------------------------------------------------------------
// APPELS
// -----------------------------------------------------------------------

/** État complet du bulletin d'engagement d'un équipage (zone admin). */
export async function getCrewBedOverview(crewId: string): Promise<BedOverviewResult> {
  try {
    const { data, error } = await supabase.rpc('get_crew_bed_overview', { p_crew_id: crewId });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedCrewOverview };
  } catch (err) {
    console.error('[BED-ADMIN] get_crew_bed_overview:', err);
    return { status: 'error' };
  }
}

/**
 * Lève manuellement un élément manquant. Le serveur trace la levée dans la
 * timeline PUIS recalcule le statut BED : la réponse est déjà l'état d'après.
 */
export async function resolveBedMissingItem(
  crewId: string,
  itemCode: string,
  note?: string
): Promise<BedOverviewResult> {
  try {
    const { data, error } = await supabase.rpc('bed_resolve_missing_item', {
      p_crew_id: crewId,
      p_item_code: itemCode,
      p_note: note ?? null,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedCrewOverview };
  } catch (err) {
    console.error('[BED-ADMIN] bed_resolve_missing_item:', err);
    return { status: 'error' };
  }
}

/** Annule une levée : l'élément redevient manquant, le statut redescend. */
export async function unresolveBedMissingItem(
  crewId: string,
  itemCode: string
): Promise<BedOverviewResult> {
  try {
    const { data, error } = await supabase.rpc('bed_unresolve_missing_item', {
      p_crew_id: crewId,
      p_item_code: itemCode,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedCrewOverview };
  } catch (err) {
    console.error('[BED-ADMIN] bed_unresolve_missing_item:', err);
    return { status: 'error' };
  }
}

/**
 * Override manuel du statut BED (cahier L15) — et seul chemin vers
 * « Inscrit définitif » (cahier L41 : action administrative).
 */
export async function setCrewBedStatus(
  crewId: string,
  status: string,
  note?: string
): Promise<BedOverviewResult> {
  try {
    const { data, error } = await supabase.rpc('bed_set_crew_status', {
      p_crew_id: crewId,
      p_status: status,
      p_note: note ?? null,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedCrewOverview };
  } catch (err) {
    console.error('[BED-ADMIN] bed_set_crew_status:', err);
    return { status: 'error' };
  }
}

/** Émet le lien du FORMULAIRE BED et renvoie l'URL complète à copier. */
export async function mintBedFormLink(crewId: string, expiresInDays = 60): Promise<BedMintResult> {
  try {
    const { data, error } = await supabase.rpc('mint_bed_form_link', {
      p_crew_id: crewId,
      p_expires_in_days: expiresInDays,
    });
    if (error) return { status: classify(error.message), message: error.message };
    const row = data as { token: string; form_slug: string | null };
    if (!row?.token || !row.form_slug) {
      return { status: 'error', message: 'bed_form_not_found' };
    }
    return {
      status: 'ok',
      token: row.token,
      url: buildBedFormLink(row.form_slug, row.token),
      expiresInDays,
    };
  } catch (err) {
    console.error('[BED-ADMIN] mint_bed_form_link:', err);
    return { status: 'error' };
  }
}

/**
 * Synthèse BED d'un LOT d'équipages (listes + export CSV). Bornée aux ids
 * demandés côté serveur : jamais un balayage de toute la base.
 */
export async function fetchCrewsBedSummary(
  crewIds: string[]
): Promise<Map<string, BedCrewSummary>> {
  const result = new Map<string, BedCrewSummary>();
  if (crewIds.length === 0) return result;

  // Même découpage que les autres enrichissements de liste : on ne dépasse
  // jamais la taille d'URL/payload confortable pour PostgREST.
  const CHUNK = 500;
  for (let i = 0; i < crewIds.length; i += CHUNK) {
    const chunk = crewIds.slice(i, i + CHUNK);
    const { data, error } = await supabase.rpc('get_crews_bed_summary', { p_crew_ids: chunk });
    if (error) {
      // Un utilisateur non-staff n'a pas la zone BED : l'export doit rester
      // possible, simplement sans les colonnes BED.
      console.warn('[BED-ADMIN] get_crews_bed_summary:', error.message);
      return result;
    }
    for (const row of (data ?? []) as BedCrewSummary[]) {
      result.set(row.crew_id, row);
    }
  }
  return result;
}
